import React from 'react';
import {
  BarChart, Bar,
  PieChart, Pie,
  LineChart, Line,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer
} from 'recharts';

// Color palette for charts
const COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // green-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#14b8a6', // teal-500
  '#f97316', // orange-500
];

/**
 * ReportChart Component
 * Renders interactive charts based on report data
 */
function ReportChart({ data, chartType = 'bar', config = {} }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">📊</div>
          <p>No data to display</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = prepareChartData(data, config);

  // Render based on chart type
  switch (chartType) {
    case 'bar':
      return renderBarChart(chartData, config);
    case 'pie':
      return renderPieChart(chartData, config);
    case 'line':
      return renderLineChart(chartData, config);
    default:
      return renderBarChart(chartData, config);
  }
}

/**
 * Prepare data for charting
 * Intelligently selects fields for X-axis (labels) and Y-axis (values)
 */
function prepareChartData(data, config) {
  if (!data || data.length === 0) return [];

  // Get field names
  const fields = Object.keys(data[0]);
  
  // Try to find appropriate fields for charting
  // X-axis: first text/string field
  // Y-axis: first numeric field
  
  const textFields = fields.filter(f => {
    const value = data[0][f];
    return typeof value === 'string' || value === null;
  });
  
  const numericFields = fields.filter(f => {
    const value = data[0][f];
    return typeof value === 'number';
  });

  const labelField = config.xField || textFields[0] || fields[0];
  const valueField = config.yField || numericFields[0] || fields[1] || fields[0];

  // Transform data
  return data.slice(0, 20).map(row => ({
    name: String(row[labelField] || 'Unknown'),
    value: parseFloat(row[valueField]) || 0,
    ...row // Include all original fields for tooltip
  }));
}

/**
 * Render Bar Chart
 */
function renderBarChart(data, config) {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="name" 
          angle={-45}
          textAnchor="end"
          height={100}
          interval={0}
          style={{ fontSize: '12px' }}
        />
        <YAxis style={{ fontSize: '12px' }} />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#fff', 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px'
          }}
          formatter={(value) => formatValue(value)}
        />
        <Legend />
        <Bar 
          dataKey="value" 
          fill={COLORS[0]} 
          name={config.valueLabel || 'Value'}
          radius={[8, 8, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Render Pie Chart
 */
function renderPieChart(data, config) {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={true}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={120}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#fff', 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px'
          }}
          formatter={(value) => formatValue(value)}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

/**
 * Render Line Chart
 */
function renderLineChart(data, config) {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="name"
          angle={-45}
          textAnchor="end"
          height={100}
          interval={0}
          style={{ fontSize: '12px' }}
        />
        <YAxis style={{ fontSize: '12px' }} />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#fff', 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px'
          }}
          formatter={(value) => formatValue(value)}
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="value" 
          stroke={COLORS[0]}
          strokeWidth={2}
          dot={{ fill: COLORS[0], r: 4 }}
          activeDot={{ r: 6 }}
          name={config.valueLabel || 'Value'}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Format value for display
 */
function formatValue(value) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    // Check if it looks like currency
    if (value > 100 || value < -100) {
      return '£' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return value.toFixed(2);
  }
  return String(value);
}

export default ReportChart;
